import { Box } from "@mui/material";
import WidgetCard from "@arelia-components/ui-atoms/WidgetCard";
import { DataGrid } from "@arelia-components/ui-atoms/data-grid";
import SpinnerWrapper from "@arelia-components/ui-atoms/SpinnerWrapper";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from "@mui/material";
import EmptyState from "@arelia-components/ui-atoms/EmptyState";

type CampaignPerformanceRow = {
  date: string;
  campaignName: string;
  url: string;
  sent: number;
  totalClicked: number;
  clickRate: number;
};

type Props = {
  loading: boolean;
  error: boolean;
  data?: CampaignPerformanceRow[];
};

const columnConfig = [
  {
    field: "date",
    headerName: "Date",
    width: "100px",
    formatter: (params: any) => params.value ? new Date(params.value).toLocaleDateString() : "-"
  },
  {
    field: "campaignName",
    headerName: "Campaign Name",
    width: "180px"
  },
  {
    field: "url",
    headerName: "Url",
    width: "320px",
    renderer: (params: any) => (
      <a
        href={params.value}
        target="_blank"
        rel="noopener noreferrer"
        style={{ maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block" }}
      >
        {params.value}
      </a>
    )
  },
  {
    field: "sent",
    headerName: "Sent",
    width: "40px",
    align: "right" as const,
    formatter: (params: any) =>
      params.value === 0 ? "0" : params.value ?? "-"
  },
  {
    field: "delivered",
    headerName: "Delivered",
    width: "60px",
    align: "right" as const,
    formatter: (params: any) =>
      params.value === 0 ? "0" : params.value ?? "-"
  },
  {
    field: "totalClicked",
    headerName: "Total Clicked",
    width: "90px",
    align: "right" as const,
    formatter: (params: any) =>
      params.value === 0 ? "0" : params.value ?? "-"
  },
  {
    field: "clickRate",
    headerName: (
      <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
        Click Rate
        <Tooltip title="Click Rate shows how many fans clicked on the link">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </span>
    ),
    width: "80px",
    align: "right" as const,
    formatter: (params: any) =>
      params.value != null ? `${Number(params.value).toFixed(2)}%` : "-"
  }
];

export default function FansCampaignPerformancePresenter({ loading, error, data}: Props) {
  const hasData = data && data.length > 0;

  return (
    <WidgetCard
      widgetId="FANS_CAMPAIGN_PERFORMANCE"
      title="Campaign Performance"
      loading={loading}
      error={error}
      disableFavorite={false}
    >
      <Box sx={styles.container}>
        <SpinnerWrapper loading={loading}>
          {hasData ? (
            <DataGrid
              columns={columnConfig}
              rows={data}
              sx={styles.dataGrid}
            />
          ) : (
            <EmptyState minified={true} />
          )}
        </SpinnerWrapper>
      </Box>
    </WidgetCard>
  );
}

const styles = {
  container: {
    width: 1,
    height: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  dataGrid: {
    overflow: 'auto',
    maxHeight: '100%',
    flex: 1,
  },
};
